import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAemConfig } from '@as-react/common-components';
import { isClickAndCollectOnly, isInStoreOnly } from '../util/ProductUtil';
import ProductsAPI from '../api/ProductsAPI';
import DeliveryPromiseAPI from '../api/DeliveryPromiseAPI';
import { MiniPDP } from '../components/product-detail/mini-pdp/MiniPDP';
import { PopoverSize, POPOVER_ANIMATION_DURATION, usePrevious } from '@as/cotopaxi';
import ProductDetailAPI from '../api/ProductDetailAPI';
import { mergeProductPricesToProductLister, mergeProductAvailabilityToProductLister, filterDataFromObjectByKey, } from '@as-react/common-utils';
import { ProductAddToBasketLocations } from '../enums/ActionEnums';
import { basketActions } from '../redux/basket/actions/BasketActions.creators';
import { wishListActions } from '../redux/wishList/actions/WishListActions.creators';
import { makeGetWishListProducts } from '../redux/wishList/selectors/WishListSelectors';
export const MiniPDPContainer = ({ productCode, colorId, deliveryPromiseExplanations, selectedSku, displayPackShotImagesFirst, popoverSize = PopoverSize.DRAWER_LARGE, submitText, onClose, onSubmit, miniPDPProps, }) => {
    const { defaultRequestParams, servicesEndpoint, authenticationConfig, mainWebShop } = useAemConfig();
    const dispatch = useDispatch();
    const [imagesByColor, setImagesByColor] = useState(null);
    const [productTranslatedProperties, setProductTranslatedProperties] = useState(null);
    const [shouldRender, setShouldRender] = useState(false);
    const [deliveryPromise, setDeliveryPromise] = useState(null);
    const [selectedSizes, setSelectedSizes] = useState([]);
    const wishlistProducts = useSelector(makeGetWishListProducts());
    const [product, setProduct] = useState(null);
    const oldProductCode = usePrevious(productCode);
    const oldColorId = usePrevious(colorId);
    const onWishListToggle = (wishListProduct) => dispatch(wishListActions.toggleProduct(wishListProduct));
    const _onClose = () => setTimeout(() => {
        setShouldRender(false);
        onClose();
    }, POPOVER_ANIMATION_DURATION);
    const onSizeChange = (productId, selectedColorId, sku) => {
        if (selectedColorId === colorId) {
            const sizeIndex = selectedSizes.findIndex((size) => size.productId === productId && size.colorId === selectedColorId);
            if (sizeIndex !== -1) {
                selectedSizes[sizeIndex].sku = sku;
            }
            else {
                selectedSizes.push({
                    sku,
                    colorId: selectedColorId,
                    productId,
                });
            }
            setSelectedSizes(selectedSizes);
        }
    };
    const defaultSize = useMemo(() => {
        if (!product) {
            return null;
        }
        const productSKU = selectedSku ? `${selectedSku}` : product.sku ? `${product.sku}` : null;
        const sizeIndex = selectedSizes.findIndex((size) => size.productId === product.productId && size.colorId === colorId);
        return sizeIndex === -1 ? productSKU : selectedSizes[sizeIndex].sku;
    }, [colorId, product, selectedSizes]);
    const clickAndCollectOnly = product ? isClickAndCollectOnly(product) : false;
    const inStoreOnly = product ? isInStoreOnly(product) : false;
    const sortImages = (_images) => {
        if (displayPackShotImagesFirst) {
            const packShotImages = [];
            const modelImages = [];
            _images.forEach(image => {
                image.picture.includes('_packshot') ? packShotImages.push(image) : modelImages.push(image);
            });
            return packShotImages.concat(modelImages.sort((x, y) => x.sortOrder - y.sortOrder));
        }
        return _images.sort((x, y) => x.sortOrder - y.sortOrder);
    };
    const getPriceOverlayByColor = (colors) => colors.reduce((acc, color) => (Object.assign(Object.assign({}, acc), { [color.colourId]: color.priceOverlay })), {});
    const fetchProductData = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        let productData = null;
        let productTileData = null;
        let productPrice = null;
        let productAvailability = null;
        setImagesByColor(null);
        try {
            productData = yield ProductsAPI.getProductByProductCode(servicesEndpoint, mainWebShop, productCode);
        }
        catch (_b) { }
        if (!productData) {
            return;
        }
        setProductTranslatedProperties(filterDataFromObjectByKey(productData.productTranslatedProperties, defaultRequestParams.language));
        try {
            productTileData = yield ProductsAPI.getSingleProductData(servicesEndpoint, mainWebShop, defaultRequestParams.anaLang, defaultRequestParams.language, defaultRequestParams.shopId, productCode, colorId.toString(), undefined, true);
        }
        catch (_c) { }
        if (!productTileData) {
            return;
        }
        try {
            productPrice = yield ProductDetailAPI.getProductDetailPriceByProductId(servicesEndpoint, defaultRequestParams.mainWebshop, defaultRequestParams.shopId, productTileData.productId);
        }
        catch (_d) { }
        if (!productPrice) {
            return;
        }
        try {
            productAvailability = yield ProductDetailAPI.getProductDetailAvailabilityByProductId(servicesEndpoint, defaultRequestParams.mainWebshop, String(productTileData.productId));
        }
        catch (_e) { }
        if (productAvailability) {
            productTileData = mergeProductAvailabilityToProductLister(productTileData, productAvailability);
        }
        const mergedProduct = mergeProductPricesToProductLister(productTileData, productPrice);
        const selectedProductColor = productTileData.colours.find(color => color.colourId === colorId);
        const possibleSkus = selectedProductColor === null || selectedProductColor === void 0 ? void 0 : selectedProductColor.sizes.map((productColorSize) => productColorSize.sku);
        if ((possibleSkus === null || possibleSkus === void 0 ? void 0 : possibleSkus.length) === 1) {
            mergedProduct.sku = parseInt(possibleSkus[0], 10);
        }
        setProduct(mergedProduct);
        const images = (_a = productData.productColorVariations) === null || _a === void 0 ? void 0 : _a.map((color) => {
            const colorVariation = productData.productColorVariations.find((tmpColorVariation) => color.colorId === tmpColorVariation.colorId);
            return {
                colorId: colorVariation.colorId,
                images: colorVariation.images,
            };
        });
        if (!!images && images.length > 0) {
            const sortedImages = images.map(image => ({
                colorId: image.colorId,
                images: sortImages(image.images),
            }));
            setImagesByColor(sortedImages);
            setShouldRender(true);
        }
    });
    const fetchDeliveryPromise = (productId, skuId) => __awaiter(void 0, void 0, void 0, function* () {
        const fetchedDeliveryPromise = yield DeliveryPromiseAPI.getDeliveryPromise(servicesEndpoint, skuId, productId, authenticationConfig);
        !!fetchedDeliveryPromise && setDeliveryPromise(fetchedDeliveryPromise);
    });
    const removeDeliveryPromise = () => setDeliveryPromise(null);
    const handleAddToBasketMiniPdp = (extensiveTileProduct, location) => {
        dispatch(basketActions.createAddExtensiveTileProductAction(extensiveTileProduct, location));
    };
    const handleOnSubmit = (selectedColorId, sku) => {
        const basketProduct = { product: product, colorId: selectedColorId, sku };
        if (onSubmit) {
            onSubmit(basketProduct);
        }
        else {
            handleAddToBasketMiniPdp(basketProduct, ProductAddToBasketLocations.MINI_PDP);
        }
    };
    useEffect(() => {
        if (productCode && productCode !== oldProductCode && colorId && colorId !== oldColorId) {
            fetchProductData();
        }
    }, [productCode, oldProductCode, colorId, oldColorId]);
    if (!shouldRender || !imagesByColor) {
        return null;
    }
    return (_jsx(MiniPDP, Object.assign({ isOpen: !!product && !!colorId, onSubmit: handleOnSubmit, colorId: colorId, clickAndCollectOnly: clickAndCollectOnly, inStoreOnly: inStoreOnly, productId: product.productId, productBrand: product.brand, productCode: product.productCode, productTitle: product.title, productColors: product.colours, productImagesByColor: imagesByColor, onClose: _onClose, onSizeChange: onSizeChange, seoUrl: product.seoUrl, defaultSize: defaultSize, getDeliveryPromise: fetchDeliveryPromise, removeDeliveryPromise: removeDeliveryPromise, deliveryPromise: deliveryPromise, deliveryPromiseExplanations: deliveryPromiseExplanations, wishlistProducts: wishlistProducts, onWishListToggle: onWishListToggle, popoverSize: popoverSize, submitText: submitText, genderFeatureId: product.featureValueGenderId, ourPlanet: Boolean(product.ourPlanet), description: productTranslatedProperties.information, priceOverlayByColor: getPriceOverlayByColor(product.colours) }, miniPDPProps)));
};
